/* eslint-disable camelcase */

import ReduxService from 'src/redux/redux.service';
import { RootState } from 'src/redux/redux-store';
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { createShare } from 'src/services/surveys/surveys.helpers';
import { APITagType } from 'src/redux/api/invisibly-api.types';

const getUrl = (url: string) => {
  const state = ReduxService.Store.getState() as RootState;
  const isClientActive = state.userState.activeClient;
  return isClientActive ? url.replace('b2b', 'client') : url;
};

export interface MarketOutcome {
  label: string;
  probability: number;
}

export interface NormalizedMarket {
  provider: string;
  provider_market_id: string;
  title: string;
  subtitle?: string | null;
  url: string;
  outcomes?: MarketOutcome[];
  primary_probability?: number | null;
  volume_usd?: number | null;
  open_interest?: number | null;
  close_time?: string | null;
  status: string;
  fetched_at: string;
  thin_market?: boolean;
  relevance_note?: string;
}

export interface MarketSearchResponse {
  candidates: NormalizedMarket[];
  partial: boolean;
  provider_errors?: string[];
}

export interface AttachedMarket {
  id: string;
  provider: string;
  provider_market_id: string;
  relevance_note?: string | null;
  display_order: number;
  market: NormalizedMarket;
  stale: boolean;
  snapshot_timestamp?: string | null;
}

export interface AttachMarketBody {
  provider: string;
  provider_market_id: string;
  relevance_note?: string | null;
  snapshot: NormalizedMarket;
  survey_question_id?: string | null;
}

const predictionMarketsEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    searchPredictionMarkets: builder.mutation<MarketSearchResponse, { ext_survey_id: string; share?: boolean }>({
      query: ({ ext_survey_id, share }) => ({
        method: 'post',
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/market-search`), share ?? false, 'b2b'),
      }),
      transformResponse(response: { item: MarketSearchResponse }): MarketSearchResponse {
        return response.item;
      },
    }),
    getAttachedMarkets: builder.query<AttachedMarket[], { ext_survey_id: string; share?: boolean }>({
      query: ({ ext_survey_id, share }) => ({
        method: 'get',
        url: createShare(getUrl(`b2b/survey/${ext_survey_id}/markets`), share ?? false, 'b2b'),
      }),
      transformResponse(response: { item: AttachedMarket[] }): AttachedMarket[] {
        return Array.isArray(response?.item) ? response.item : [];
      },
      providesTags: (_result, _error, arg) => [
        { type: APITagType.PredictionMarkets, id: arg.ext_survey_id },
      ],
    }),
    attachPredictionMarket: builder.mutation<AttachedMarket, { ext_survey_id: string; body: AttachMarketBody }>({
      query: ({ ext_survey_id, body }) => ({
        method: 'post',
        url: getUrl(`b2b/survey/${ext_survey_id}/markets`),
        data: body,
      }),
      transformResponse(response: { item: AttachedMarket }): AttachedMarket {
        return response.item;
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: APITagType.PredictionMarkets, id: arg.ext_survey_id },
      ],
    }),
    reorderPredictionMarket: builder.mutation<
    AttachedMarket,
    { ext_survey_id: string; attachment_id: string; display_order: number }
    >({
      query: ({ ext_survey_id, attachment_id, display_order }) => ({
        method: 'patch',
        url: getUrl(`b2b/survey/${ext_survey_id}/markets/${attachment_id}`),
        data: { display_order },
      }),
      transformResponse(response: { item: AttachedMarket }): AttachedMarket {
        return response.item;
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: APITagType.PredictionMarkets, id: arg.ext_survey_id },
      ],
    }),
    detachPredictionMarket: builder.mutation<void, { ext_survey_id: string; attachment_id: string }>({
      query: ({ ext_survey_id, attachment_id }) => ({
        method: 'delete',
        url: getUrl(`b2b/survey/${ext_survey_id}/markets/${attachment_id}`),
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: APITagType.PredictionMarkets, id: arg.ext_survey_id },
      ],
    }),
  }),
});

export const {
  useSearchPredictionMarketsMutation,
  useGetAttachedMarketsQuery,
  useAttachPredictionMarketMutation,
  useReorderPredictionMarketMutation,
  useDetachPredictionMarketMutation,
} = predictionMarketsEndpoints;

export { predictionMarketsEndpoints };
